/**
 * Maps booking-request (n8n workflow) failures returned by
 * `PATCH /api/v1/activities/status` onto translatable feedback.
 *
 * The backend answers with a `BusinessError` body: `{ message, errorCode, ...metadata }`.
 * We key off `errorCode` rather than `message`, because `message` is server-translated
 * and therefore not a stable contract.
 *
 * A rejected booking request is FINAL for that execution — the workflow writes no activity
 * status and nothing blocks a resubmission, so an unchanged retry fails on the same rule.
 * None of these strings may tell the user to simply try again; they name what to fix.
 */

export const BOOKING_REQUEST_ERROR_CODE = {
  /** The workflow's client/user gate refused the caller. Configuration, not user error. */
  WORKFLOW_REJECTED: 'CFE000047',
  /** No answer within the outbound timeout. It may still be running. */
  WORKFLOW_TIMEOUT: 'CFE000048',
  /** Endpoint unreachable / blocked. Configuration problem. */
  WORKFLOW_UNREACHABLE: 'CFE000049',
  /** The activities already belong to a booking request that is still running. */
  DUPLICATE: 'CFE000050',
  /** A scheduling business rule refused the request; `validationCode` says which. */
  VALIDATION_FAILED: 'CFE000051',
} as const;

/**
 * The rules the workflow enforces. Kept in sync with the contract at
 * `booking-request-handoff/docs/30-contracts/phase0-http-responses.md`.
 */
export const BOOKING_VALIDATION_CODES = [
  'INVALID_ACTIVITY_STATUS',
  'MULTIPLE_PROJECTS',
  'MULTIPLE_SITES',
  'MIXED_VISIT_TYPES',
  'PRE_REQUEST_TOO_CLOSE',
  'NO_SITE_CONTACT',
  'BOOKING_REQUEST_VALIDATION_FAILED',
] as const;

export type BookingValidationCode = (typeof BOOKING_VALIDATION_CODES)[number];

const FEEDBACK_KEY_BY_CODE: Record<string, string> = {
  [BOOKING_REQUEST_ERROR_CODE.WORKFLOW_REJECTED]: 'BOOKING_REQUEST_WORKFLOW_REJECTED',
  [BOOKING_REQUEST_ERROR_CODE.WORKFLOW_TIMEOUT]: 'BOOKING_REQUEST_WORKFLOW_TIMEOUT',
  [BOOKING_REQUEST_ERROR_CODE.WORKFLOW_UNREACHABLE]:
    'BOOKING_REQUEST_WORKFLOW_UNREACHABLE',
  [BOOKING_REQUEST_ERROR_CODE.DUPLICATE]: 'BOOKING_REQUEST_DUPLICATE',
  [BOOKING_REQUEST_ERROR_CODE.VALIDATION_FAILED]: 'BOOKING_REQUEST_VALIDATION_FAILED',
};

/**
 * Older builds and any non-BusinessError path put the key in `message` instead of
 * `errorCode`, so accept that too rather than silently falling back to the generic toast.
 */
const CODE_BY_FEEDBACK_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(FEEDBACK_KEY_BY_CODE).map(([code, key]) => [key, code])
);

/** Feedback strings that interpolate a `{{reference}}` and so need a `_NO_REF` fallback. */
const REFERENCE_BEARING_KEYS = new Set([
  'BOOKING_REQUEST_WORKFLOW_REJECTED',
  'BOOKING_REQUEST_WORKFLOW_UNREACHABLE',
]);

export type BookingRequestFeedback = {
  /** i18n key under `common:feedbackCodes.*`. */
  key: string;
  /** Interpolation values, e.g. the support reference. */
  values?: Record<string, unknown>;
};

type ApiErrorShape = {
  errorCode?: unknown;
  message?: unknown;
  webhookLogId?: unknown;
  validationCode?: unknown;
  duplicateOf?: unknown;
};

function resolveCode(error: ApiErrorShape): string | undefined {
  if (typeof error.errorCode === 'string' && FEEDBACK_KEY_BY_CODE[error.errorCode]) {
    return error.errorCode;
  }
  if (typeof error.message === 'string' && CODE_BY_FEEDBACK_KEY[error.message]) {
    return CODE_BY_FEEDBACK_KEY[error.message];
  }
  return undefined;
}

function isKnownValidationCode(value: unknown): value is BookingValidationCode {
  return (
    typeof value === 'string' &&
    (BOOKING_VALIDATION_CODES as readonly string[]).includes(value)
  );
}

/**
 * Returns translatable feedback for a booking-request workflow failure, or `null`
 * when the error is something else and the caller's generic message should stand.
 */
export function mapBookingRequestError(error: unknown): BookingRequestFeedback | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const apiError = error as ApiErrorShape;
  const code = resolveCode(apiError);
  if (!code) {
    return null;
  }

  const reference =
    typeof apiError.webhookLogId === 'number' ? apiError.webhookLogId : undefined;

  // A business-rule rejection is the one case where we can tell the user what to change,
  // so it gets a per-rule string. An unrecognised rule (the workflow may add one) falls
  // back to the generic validation wording rather than showing a raw code.
  if (code === BOOKING_REQUEST_ERROR_CODE.VALIDATION_FAILED) {
    const rule = isKnownValidationCode(apiError.validationCode)
      ? apiError.validationCode
      : 'BOOKING_REQUEST_VALIDATION_FAILED';
    return { key: `common:feedbackCodes.BOOKING_REQUEST_RULE_${rule}` };
  }

  if (code === BOOKING_REQUEST_ERROR_CODE.DUPLICATE) {
    const duplicateOf =
      typeof apiError.duplicateOf === 'string' || typeof apiError.duplicateOf === 'number'
        ? String(apiError.duplicateOf)
        : undefined;
    return duplicateOf
      ? {
          key: 'common:feedbackCodes.BOOKING_REQUEST_DUPLICATE_WITH_REF',
          values: { reference: duplicateOf },
        }
      : { key: 'common:feedbackCodes.BOOKING_REQUEST_DUPLICATE' };
  }

  let feedbackKey = FEEDBACK_KEY_BY_CODE[code];

  // The REJECTED / UNREACHABLE strings quote a support reference. When the backend could
  // not give us one (e.g. the log row was never written), fall back to the variant without
  // it rather than rendering a literal `{{reference}}`.
  if (reference == null && REFERENCE_BEARING_KEYS.has(feedbackKey)) {
    feedbackKey = `${feedbackKey}_NO_REF`;
  }

  return {
    key: `common:feedbackCodes.${feedbackKey}`,
    values: reference != null ? { reference } : undefined,
  };
}
